import { Marker, Meeting } from '@/types.d';
import { CompassContextType } from '@/src/libs/CompassContext';
import pin from '@/public/images/pin.svg';
import pinFocus from '@/public/images/pin-focus.svg';
import dot from '@/public/images/dot.svg';

import { getMarkerAltText, getMarkerKind, MarkerKind } from './markerKind';

interface MarkerProps {
  class: typeof google.maps.marker.AdvancedMarkerElement | null;
  coords: google.maps.LatLngLiteral;
  ctx?: CompassContextType;
  id: string;
  meetings: Meeting[];
  map: google.maps.Map;
}

const buildMarker = ({
  class: advancedMarkerClass,
  coords,
  ctx,
  id,
  meetings,
  map,
}: MarkerProps): null | Marker => {
  if (!ctx) throw new Error('No context provided');
  if (!map) throw new Error('No map provided');
  if (!advancedMarkerClass) throw new Error('No AdvancedMarkerClass provided');

  const options = {
    gmpClickable: true,
    map: map,
    position: coords,
    zIndex: 10,
  };

  const marker = new advancedMarkerClass(options);

  marker.addListener('click', () => {
    map.panTo(meetings[0].coords);
    ctx.dispatch({
      type: 'SET_MEETING_FOCUS',
      payload: id,
    });
  });

  const kind = getMarkerKind(meetings.length, ctx?.state?.meetingFocus === id);
  const altText = getMarkerAltText(meetings);

  setMarkerImage(marker, kind, altText);

  return {
    markerId: id,
    coords,
    marker: marker,
    meetings,
  };
};

const MIN_TAP_SIZE = 44;

const setMarkerImage = (
  marker: google.maps.marker.AdvancedMarkerElement,
  kind: MarkerKind,
  altText: string
) => {
  const isSelected = kind === 'selected';
  const src = { dot: dot.src, pin: pin.src, selected: pinFocus.src }[kind];
  const [width, height] = {
    dot: [10, 10],
    pin: [18, 22],
    selected: [36, 44],
  }[kind];

  const image = document.createElement('img');
  image.src = src;
  image.width = width;
  image.height = height;
  image.alt = altText;
  image.style.display = 'block';

  // The marker anchors the bottom-centre of its content on the coordinate.
  // Pins have their tip at the bottom, so they sit at the wrapper's bottom
  // centre. A dot is centred instead, so the wrapper is shifted down by half
  // its height to put the dot's centre on the coordinate.
  const wrapper = document.createElement('div');
  wrapper.style.display = 'flex';
  wrapper.style.justifyContent = 'center';
  wrapper.style.alignItems = kind === 'dot' ? 'center' : 'flex-end';
  wrapper.style.minWidth = `${MIN_TAP_SIZE}px`;
  wrapper.style.minHeight = `${MIN_TAP_SIZE}px`;
  wrapper.style.cursor = 'pointer';
  if (kind === 'dot') wrapper.style.transform = 'translateY(50%)';
  wrapper.appendChild(image);

  marker.content = wrapper;
  marker.zIndex = isSelected ? 100 : 10;
};

export { buildMarker, setMarkerImage };
